import { Attrs, EventListeners, MinzeElement } from 'minze'
import { Locale } from '../../../../modules/locale'
import { mapEvent, getCardPlaceholder } from '../../../../modules/utils'
import { LvcPrimarySliderItem } from '../../../../modules/sliderUtils'
import { LvcItemSourceData } from '../../../../../types/global'
import { formatDate } from '../../../../modules/date'
import { renderDateElement, renderTicketButton } from '../../../../componentsV2/lvc-card/utils'
import { EventStatus } from '../../../../componentsV2/lvc-card/lvc-event-status'

export interface LvcSearchResultBlock {
  results: string
  dataModal: string
  dataComponentLocation: string
}
interface StartDay {
  day: string | null
  month: string | null
  monthFull: string | null
  year: string | null
}
export interface LvcSearchResultBlockDateBlock {
  startDay: StartDay | null
  items: LvcPrimarySliderItem[]
}

export class LvcSearchResultBlock extends MinzeElement {
  attrs: Attrs = ['results', 'data-modal', ['enable-date-blocks', true], 'data-component-location']
  static observedAttributes = ['results', 'data-modal'] // Required don't remove.

  private mapSourceDataToItems(encodedDataString: string) {
    const data = JSON.parse(decodeURIComponent(encodedDataString)) as LvcItemSourceData[]
    return data ? data.map(mapEvent) : []
  }

  private compileDateBlocks(items: LvcPrimarySliderItem[]): LvcSearchResultBlockDateBlock[] {
    if (this.dataModal !== 'events_search') {
      return [{ startDay: null, items }]
    }

    const dateBlocks = items.reduce((blocks, item) => {
      const lastBlock = blocks[blocks.length - 1]
      if (lastBlock && lastBlock[0].startDay === item.startDay) {
        lastBlock.push(item)
      } else {
        blocks.push([item])
      }
      return blocks
    }, [] as LvcPrimarySliderItem[][])

    return dateBlocks.map(block => ({
      startDay: block[0]?.startDate
        ? {
            day: formatDate(block[0].startDate, { day: 'numeric' }, Locale.language),
            month: formatDate(block[0].startDate, { month: 'short' }, Locale.language),
            monthFull: formatDate(block[0].startDate, { month: 'long' }, Locale.language),
            year: formatDate(block[0].startDate, { year: 'numeric' }, Locale.language),
          }
        : null,
      items: block,
    }))
  }

  private renderResultCard(item: LvcPrimarySliderItem) {
    if (!item) return ''
    const { name, location, city, uiAdditionalTypes, sameDayOffers, cardImage, schema, festivals } =
      item
    return /*html*/ `
      <lvc-search-result-card href="${item.urlConstruct?.[Locale.language]}" data-component-location="${this.dataComponentLocation || 'recherche'}" >
        ${
          festivals && festivals.length > 0
            ? /*html*/ `
          <div slot="festival" class="custom-festival-badge">${festivals[0].nameFr}</div>
          `
            : ''
        }
        <div slot="title">${name?.[Locale.language]}</div>
        ${renderDateElement(item, item?.status, item?.previousStartDate)}
        ${
          location?.[Locale.language]
            ? `<lvc-event-location
            slot="location"
            text="${[location?.[Locale.language], city?.[Locale.language]].filter(Boolean).join(', ')}"
          ></lvc-event-location>`
            : ''
        }
        ${uiAdditionalTypes ? `<lvc-tags slot="tag" data-discipline-tags="${encodeURIComponent(JSON.stringify(uiAdditionalTypes))}"></lvc-tags>` : ''}
        ${sameDayOffers?.length ? `<lvc-event-show-more slot="more" items="${encodeURIComponent(JSON.stringify(sameDayOffers))}"></lvc-event-show-more>` : ''}
        ${renderTicketButton(item, this.dataComponentLocation)}
        ${item.recommendedBy && item.recommendedBy.length > 0 ? `<lvc-recommended slot="recommendations" recommended-list="${encodeURIComponent(JSON.stringify(item.recommendedBy))}"></lvc-recommended>` : ''}
        ${
          item.status !== EventStatus.Cancelled
            ? `<lvc-favorite-icon-button
          data-id="${item?.id || ''}"
          data-type="${item?.schema || ''}"
          data-gtm-name="${item?.name?.[Locale.language] || ''}"
          data-gtm-brand="${item?.artist?.[Locale.language] || ''}"
          data-gtm-category="${Array.isArray(item?.uiAdditionalTypes) ? item.uiAdditionalTypes.map(type => type.code).join(', ') : ''}"
          data-gtm-category2="${item?.location?.[Locale.language] || ''}"
          data-gtm-category3="${item?.formattedDateTime || ''}"
          data-gtm-category4="${item?.isAccessibleForFree ? 'Gratuit' : 'Billet'}"
          slot="favorite">
          </lvc-favorite-icon-button>
        `
            : ''
        }
        ${
          cardImage
            ? `
          <lvc-image slot="image" src="${cardImage}" data-image-placeholder="${getCardPlaceholder(schema)}"></lvc-image>
        `
            : ''
        }
      </lvc-search-result-card>
    `
  }

  private renderDateBlocks(blocks: LvcSearchResultBlockDateBlock[]) {
    return blocks
      .map(
        ({ startDay, items }) => `
          <div class="lvc-search-results-block__flex-wrapper">
            ${this.enableDateBlocks && startDay ? this.renderStartDay(startDay) : ''}
            <div class="lvc-search-results-block__items-wrapper">
              ${items.map(item => this.renderResultCard(item)).join('')}
            </div>
          </div>`
      )
      .join('')
  }

  private renderStartDay(startDay: StartDay) {
    if (!startDay) return ''
    return `
      <div class="lvc-search-results-block__start-day-divider">
        <div class="lvc-search-results-block__start-day-text">${startDay.day} ${startDay.monthFull} ${startDay.year}</div>
      </div>
      <div class="lvc-search-results-block__sticky-wrapper">
        <div class='lvc-search-results-block__sticky-block'>
          <div class='lvc-search-results-block__sticky-block-day'>${startDay.day}</div>
          <div class='lvc-search-results-block__sticky-block-month'>${startDay.month}</div>
          <div class='lvc-search-results-block__sticky-block-year'>${startDay.year}</div>
        </div>
      </div>`
  }

  html = () => `
    <div class="lvc-search-results-block__wrapper">
      ${this.results ? this.renderDateBlocks(this.compileDateBlocks(this.mapSourceDataToItems(this.results))) : ''}
    </div>
  `

  css = () => `
    :host {
    }
    .custom-festival-badge {
      /* box-shadow: 2px 3px 7px rgba(0, 0, 0, 0.25); */
      color: white;
      background: #1e007d;
      border-radius: 12px;
      padding: 1px 7px;
      font-size: 10px;
      font-weight: 500;
    }
    .lvc-search-results-block__flex-wrapper {
      display: flex; 
      gap: 2rem; 
    }

    .lvc-search-results-block__sticky-wrapper { 
      position: relative; 
    }

    .lvc-search-results-block__sticky-wrapper::after { 
      content: ''; 
      position: absolute; 
      margin: 0 auto; 
      left: 0; 
      right: 0; 
      width: 2px; 
      background: rgba(30, 0, 125, 0.2); 
      height: 100%; 
    }

    .lvc-search-results-block__items-wrapper { 
      flex-grow: 1; 
    }

    .lvc-search-results-block__sticky-block-day { 
      font-size: 22px; 
      font-weight: 800; 
    }

    .lvc-search-results-block__sticky-block-month,
    .lvc-search-results-block__sticky-block-year { 
      font-size: 12px; 
    }

    .lvc-search-results-block__start-day-text { 
      font-size: 1rem; 
      font-weight: 500; 
      text-align: center; 
      padding: 6px 18px; 
      background: rgba(255, 255, 255, 0.5); 
      border-radius: 999px; 
    }

    .lvc-search-results-block__start-day-divider { 
      display: none; 
      justify-content: center; 
      margin: 4rem 0 1rem; 
    }

    .lvc-search-results-block__sticky-block { 
      margin-top: 1rem; 
      position: sticky; 
      line-height: 1.1; 
      text-align: center; 
      top: 5.5rem; 
      background: white; 
      z-index: 1; 
      padding: 0.5rem 1rem; 
      border-radius: 18px; 
    }

    @media screen and (max-width: 991px) { 
      .lvc-search-results-block__flex-wrapper { 
        display: block; 
      }

      .lvc-search-results-block__start-day-divider { 
        display: flex; 
      }

      .lvc-search-results-block__sticky-wrapper { 
        display: none; 
      }
    }

    @media screen and (max-width: 575px) { 
      .lvc-search-results-block__start-day-text { 
        font-size: 12px; 
      }

      .lvc-search-results-block__start-day-divider { 
        padding: 0 0.5rem; 
        margin-top: 2rem; 
      }
    }
  `
  // Listen for updates to recommended content (ex:auth change etc), rerender
  eventListeners: EventListeners = [
    [window, 'lvc:recommended_content_updated', () => this.rerender()],
  ]
}
